import React from 'react';
import { useState } from 'react';
import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import {
  SiInstagram,
  SiTiktok,
  SiSnapchat,
  SiX,
  SiLinkedin,
} from 'react-icons/si';
import { useI18n } from './i18n';
import { 
  BarChart3, 
  BrainCircuit,
  Building2,
  ChevronDown,
  Code2,
  Cloud,
  Lightbulb, 
  Cpu,
  Utensils, 
  Pill,
  Shield, 
  Hand, 
  Globe,
  UserPlus,
  Users,
  Smartphone,
  TrendingUp
} from 'lucide-react';
import LanguageToggle from './components/LanguageToggle';
import Contact from './pages/Contact';
import ContactAr from './pages/ContactAr';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import PrivacyPolicyAr from './pages/PrivacyPolicyAr';
import TermsAr from './pages/TermsAr';
import { WaitlistForm } from './components/WaitlistForm';

function Header() {
  const { lang, translations } = useI18n();
  const location = useLocation();
  const [companyMenuOpen, setCompanyMenuOpen] = useState(false);
  const companyPath = lang === 'ar' ? '/ar/company' : '/company';
  const isCompanyPage = location.pathname.includes('/company');

  return (
    <header className="px-4 py-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between gap-4">
        {isCompanyPage ? (
          <div className="min-w-0 flex-1" aria-hidden="true" />
        ) : (
          <Link to="/" aria-label="Vitalo home" className="shrink-0">
            <img
              src="/Vitalo_no_Background-removebg-preview.png"
              alt={translations.meta.title}
              className="h-12 md:h-16 w-auto"
            />
          </Link>
        )}

        <nav className="flex items-center gap-3" aria-label={translations.nav.mainLabel}>
          {isCompanyPage && (
            <Link
              to="/"
              className="hidden rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-50 sm:inline-flex"
            >
              {translations.nav.vitalo}
            </Link>
          )}

          <div className="relative">
            <button
              type="button"
              onClick={() => setCompanyMenuOpen((open) => !open)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-200"
              aria-haspopup="true"
              aria-expanded={companyMenuOpen}
            >
              <span>{translations.nav.company}</span>
              <ChevronDown className="h-4 w-4" aria-hidden="true" />
            </button>

            <div className={`${companyMenuOpen ? 'visible translate-y-0 opacity-100' : 'invisible translate-y-1 opacity-0'} absolute end-0 top-full z-20 mt-2 w-56 rounded-xl border border-gray-100 bg-white p-2 shadow-lg transition-all duration-150`}>
              <Link
                to={companyPath}
                onClick={() => setCompanyMenuOpen(false)}
                className="block rounded-lg px-3 py-2 text-sm font-medium text-slate-800 transition-colors duration-150 hover:bg-teal-50 hover:text-teal-700"
              >
                {translations.nav.whoWeAre}
              </Link>
            </div>
          </div>

          <LanguageToggle />
        </nav>
      </div>
    </header>
  );
}

function HomePage() {
  const { lang, translations } = useI18n();
  const [showWaitlist, setShowWaitlist] = useState(false);
  const APP_STORE_URL = 'https://apps.apple.com/us/app/vitalo-wellness/id6754817873';

  const openAndroidWaitlist = (location: string) => {
    window.plausible?.('Android Waitlist CTA Click', { props: { location } });
    setShowWaitlist(true);
  };

  // Device logos for carousel
  const deviceLogos = [
    { src: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/apple.svg', alt: 'Apple' },
    { src: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/samsung.svg', alt: 'Samsung' },
    { src: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/fitbit.svg', alt: 'Fitbit' },
    { src: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/garmin.svg', alt: 'Garmin' },
    { src: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/huawei.svg', alt: 'Huawei' },
    { src: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/xiaomi.svg', alt: 'Xiaomi' },
    { src: 'https://upload.wikimedia.org/wikipedia/commons/8/8f/Oura_logo.svg', alt: 'Oura' },
    { src: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/withings.svg', alt: 'Withings' },
    { src: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/polar.svg', alt: 'Polar' },
    { src: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/suunto.svg', alt: 'Suunto' },
    { src: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/googlefit.svg', alt: 'Google Fit' },
  ];

  // Set meta tags for SEO
  useEffect(() => {
    document.title = translations.meta.title;
    
    // Update or create meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', translations.meta.description);
    
    // Set language and direction
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }, [lang, translations]);

  return (
    <div className="min-h-screen bg-white">
      {/* Schema Markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://vitaloapp.com/#organization",
              "name": "Vitalo",
              "url": "https://vitaloapp.com",
              "logo": {
                "@type": "ImageObject",
                "url": "https://vitaloapp.com/Vitalo_no_Background-removebg-preview.png"
              },
              "description": translations.meta.description
            },
            {
              "@type": "SoftwareApplication",
              "@id": "https://vitaloapp.com/#app",
              "name": translations.meta.title,
              "operatingSystem": ["iOS", "Android"],
              "description": translations.meta.description,
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              },
              "applicationCategory": "HealthApplication",
              "keywords": translations.meta.description
            },
            {
              "@type": "FAQPage",
              "@id": "https://vitaloapp.com/#faq",
              "mainEntity": []
            }
          ]
        })
      }} />

      <Header />

      {/* Hero Section */}
      <section className="px-4 py-8 md:py-16 max-w-7xl mx-auto">
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-semibold text-slate-900 mb-4 leading-tight" itemProp="headline">
            {translations.hero.title}
          </h1>
          <h2 className="text-2xl md:text-3xl font-medium text-teal-600 mb-4">
            {translations.hero.subtitle}
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 mb-6 font-normal" itemProp="description">
            {translations.hero.supporting}
          </p>
          
          {/* App Store Badges */}
          <div className="text-center mb-8">
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center sm:items-start">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download on the App Store"
                className="block"
              >
                <img
                  src="/Download_on_the_App_Store_Badge_US-UK_RGB_wht_092917.svg"
                  alt="Download on the App Store"
                  className="h-[44px] w-auto hover:opacity-80 transition-opacity duration-200"
                />
              </a>
              <div className="flex flex-col items-center">
                <img
                  src="/GetItOnGooglePlay_Badge_Web_color_English.png"
                  alt="Get it on Google Play"
                  className="h-[44px] w-auto opacity-50"
                />
                <p className="text-sm text-gray-500 mt-1">{translations.badge.androidComingSoon}</p>
                <button
                  type="button"
                  onClick={() => openAndroidWaitlist('hero')}
                  className="mt-2 inline-flex items-center gap-2 rounded-full border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-700 transition-colors duration-200 hover:border-teal-300 hover:bg-teal-100"
                >
                  <Smartphone className="h-4 w-4" />
                  <span>{translations.badge.joinAndroidWaitlist}</span>
                </button>
              </div>
            </div>
          </div>
          
          {/* Device Carousel Section */}
          <section className="bg-[#2a2a2a] py-[50px] my-[60px] overflow-hidden">
            <div className="max-w-7xl mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-semibold text-white text-center mb-12">
                {translations.device_carousel.title}
              </h2>
              
              <div className="relative w-full overflow-hidden">
                <div className="flex carousel-track py-4">
                  {/* First set of logos */}
                  {deviceLogos.map((logo, index) => (
                    <img
                      key={`first-${index}`}
                      src={logo.src}
                      alt={logo.alt}
                      className="w-[120px] h-[60px] object-contain filter brightness-0 invert opacity-70 mx-10 transition-all duration-300 ease-in-out hover:filter-none hover:opacity-100 hover:scale-110 flex-shrink-0"
                    />
                  ))}
                  {/* Duplicate set for seamless loop */}
                  {deviceLogos.map((logo, index) => (
                    <img
                      key={`second-${index}`}
                      src={logo.src}
                      alt={logo.alt}
                      className="w-[120px] h-[60px] object-contain filter brightness-0 invert opacity-70 mx-10 transition-all duration-300 ease-in-out hover:filter-none hover:opacity-100 hover:scale-110 flex-shrink-0"
                    />
                  ))}
                </div>
              </div>
              
              <p className="text-gray-300 text-center mt-12 text-lg">
                {translations.device_carousel.more_integrations}
              </p>
            </div>
          </section>
          
          {/* Dashboard Preview */}
          <section className="dashboard-section">
            <div className="max-w-5xl mx-auto">
            <h3 className="dashboard-title text-slate-900" itemProp="name">
              {translations.features.dashboard.title}
            </h3>
            <p className="dashboard-description">
              {translations.features.dashboard.desc}
            </p>
            <div className="dashboard-image bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <img
                src={lang === 'ar' ? '/vitalo_dashboard_preview_ar.jpg' : '/vitalo_dashboard_preview_en.jpg'}
                alt={translations.features.dashboard.desc}
                className="w-full h-auto"
                loading="eager"
                decoding="async"
              />
            </div>
            </div>
          </section>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="px-4 py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-8 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <BarChart3 className="w-8 h-8 text-teal-500" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{translations.pillars.ai.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {translations.pillars.ai.desc}
              </p>
            </div>
            
            <div className="text-center p-8 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lightbulb className="w-8 h-8 text-teal-500" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{translations.features.insights.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {translations.features.insights.desc}
              </p>
            </div>
            
            <div className="text-center p-8 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Utensils className="w-8 h-8 text-teal-500" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{translations.features.ownership.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {translations.features.ownership.desc}
              </p>
            </div>
            
            <div className="text-center p-8 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Pill className="w-8 h-8 text-teal-500" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{translations.features.supplements.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {translations.features.supplements.desc}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Vitalo Promise Section */}
      <section className="px-4 py-16">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4" itemProp="name">
              {translations.core_pillars.title}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-teal-500" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{translations.pillars.privacy.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {translations.pillars.privacy.desc}
              </p>
            </div>
            
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Hand className="w-8 h-8 text-teal-500" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{translations.pillars.control.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {translations.pillars.control.desc}
              </p>
            </div>
            
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Globe className="w-8 h-8 text-teal-500" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{translations.pillars.simplicity.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {translations.pillars.simplicity.desc}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="px-4 py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4" itemProp="name">
              {translations.how_it_works.title}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {translations.how_it_works.subtitle}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <UserPlus className="w-8 h-8 text-teal-500" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                {`1. ${translations.how_it_works.step1.title}`}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {translations.how_it_works.step1.desc}
              </p>
            </div>
            
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="w-8 h-8 text-teal-500" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                {`2. ${translations.how_it_works.step2.title}`}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {translations.how_it_works.step2.desc}
              </p>
            </div>
            
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Smartphone className="w-8 h-8 text-teal-500" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                {`3. ${translations.how_it_works.step3.title}`}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {translations.how_it_works.step3.desc}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6" itemProp="name">
            {translations.final_cta.title}
          </h2>
          <p className="text-lg text-gray-600 mb-12 leading-relaxed">
            {translations.final_cta.desc}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center sm:items-start">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download on the App Store"
              onClick={() => {
                window.plausible?.('CTA Click', { props: { location: 'final-cta' } });
              }}
              className="block"
            >
              <img
                src="/Download_on_the_App_Store_Badge_US-UK_RGB_wht_092917.svg"
                alt="Download on the App Store"
                className="h-[44px] w-auto hover:opacity-80 transition-opacity duration-200"
              />
            </a>
            <div className="flex flex-col items-center">
              <img
                src="/GetItOnGooglePlay_Badge_Web_color_English.png"
                alt="Get it on Google Play"
                className="h-[44px] w-auto opacity-50"
              />
              <p className="text-sm text-gray-500 mt-1">{translations.badge.androidComingSoon}</p>
              <button
                type="button"
                onClick={() => openAndroidWaitlist('final-cta')}
                className="mt-2 inline-flex items-center gap-2 rounded-full border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-700 transition-colors duration-200 hover:border-teal-300 hover:bg-teal-100"
              >
                <Smartphone className="h-4 w-4" />
                <span>{translations.badge.joinAndroidWaitlist}</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t mt-16">
        <div className="max-w-6xl mx-auto px-4 py-10">
          {/* Social icons */}
          <div className="flex items-center justify-center gap-6 mb-6">
            <a
              href="https://www.instagram.com/hellovitalo"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Instagram"
              className="text-gray-600 hover:text-teal-500 transition-colors duration-200"
              onClick={() => window.plausible?.('Social Link Click', { props: { platform: 'Instagram' } })}
            >
              <SiInstagram className="w-6 h-6 shrink-0" />
            </a>

            <a
              href="https://tiktok.com/@vitaloapp"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="TikTok"
              className="text-gray-600 hover:text-teal-500 transition-colors duration-200"
              onClick={() => window.plausible?.('Social Link Click', { props: { platform: 'TikTok' } })}
            >
              <SiTiktok className="w-6 h-6 shrink-0" />
            </a>

            <a
              href="https://snapchat.com/add/vitaloapp"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Snapchat"
              className="text-gray-600 hover:text-teal-500 transition-colors duration-200"
              onClick={() => window.plausible?.('Social Link Click', { props: { platform: 'Snapchat' } })}
            >
              {/* Solid ghost – correct Snapchat mark */}
              <SiSnapchat className="w-6 h-6 shrink-0" />
            </a>

            <a
              href="https://x.com/HelloVitaloapp"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="X (Twitter)"
              className="text-gray-600 hover:text-teal-500 transition-colors duration-200"
              onClick={() => window.plausible?.('Social Link Click', { props: { platform: 'X' } })}
            >
              <SiX className="w-6 h-6 shrink-0" />
            </a>

            <a
              href="https://linkedin.com/company/vitaloapp"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="LinkedIn"
              className="text-gray-600 hover:text-teal-500 transition-colors duration-200"
              onClick={() => window.plausible?.('Social Link Click', { props: { platform: 'LinkedIn' } })}
            >
              <SiLinkedin className="w-6 h-6 shrink-0" />
            </a>
          </div>

          {/* Policy links */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 mb-6">
            <a
              href={lang === 'ar' ? '/ar/privacy-policy' : '/privacy-policy'}
              className="text-gray-600 hover:text-teal-500 transition-colors duration-200"
            >
              {translations.footer.privacyPolicy}
            </a>
            <a
              href={lang === 'ar' ? '/ar/terms' : '/terms'}
              className="text-gray-600 hover:text-teal-500 transition-colors duration-200"
            >
              {translations.footer.terms}
            </a>
            <a
              href={lang === 'ar' ? '/ar/contact' : '/contact'}
              className="text-gray-600 hover:text-teal-500 transition-colors duration-200"
            >
              {translations.footer.contact}
            </a>
            <Link
              to={lang === 'ar' ? '/ar/company' : '/company'}
              className="text-gray-600 hover:text-teal-500 transition-colors duration-200"
            >
              {translations.footer.company}
            </Link>
          </div>

          {/* Copyright */}
          <p className="text-center text-gray-500 text-sm">
            © {new Date().getFullYear()} Vitalo. {translations.footer.rights}
          </p>
          <p className="mx-auto mt-3 max-w-2xl text-center text-xs leading-relaxed text-gray-400">
            {translations.footer.operator}
          </p>
        </div>
      </footer>

      {showWaitlist && (
        <WaitlistForm
          onClose={() => setShowWaitlist(false)}
          platform="android"
          source="android-waitlist"
        />
      )}

    </div>
  );
}

function CompanyPage() {
  const { lang, translations } = useI18n();
  const company = translations.company;

  const capabilities = [
    { icon: BrainCircuit, title: company.capabilities.ai.title, desc: company.capabilities.ai.desc },
    { icon: Code2, title: company.capabilities.software.title, desc: company.capabilities.software.desc },
    { icon: Cloud, title: company.capabilities.infrastructure.title, desc: company.capabilities.infrastructure.desc },
    { icon: Cpu, title: company.capabilities.systems.title, desc: company.capabilities.systems.desc },
  ];

  useEffect(() => {
    document.title = company.metaTitle;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', company.metaDescription);

    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }, [company.metaDescription, company.metaTitle, lang]);

  return (
    <div className="min-h-screen bg-white">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": "https://vitaloapp.com/#operator",
          "name": "Anan Al Majarra Advanced and Information Technology Systems",
          "url": "https://vitaloapp.com/company",
          "foundingDate": "2025",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Dammam",
            "addressCountry": "SA"
          },
          "brand": {
            "@type": "Brand",
            "name": "Vitalo Wellness",
            "url": "https://vitaloapp.com"
          }
        })
      }} />

      <Header />

      <main>
        <section className="px-4 py-12 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <div>
              <h1 className="max-w-4xl text-4xl font-semibold leading-[1.18] text-slate-900 md:text-6xl md:leading-[1.16]">
                {company.title}
              </h1>
              <p className="mt-8 max-w-3xl text-lg leading-8 text-gray-600 md:text-xl">
                {company.intro}
              </p>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-gray-50 p-6 md:p-8">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-teal-100">
                  <Building2 className="h-6 w-6 text-teal-600" aria-hidden="true" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">{company.legalName}</h2>
                  <p className="mt-3 text-gray-600 leading-7">{company.summary}</p>
                </div>
              </div>

              <div className="mt-8 rounded-xl bg-white p-5">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100">
                    <Users className="h-5 w-5 text-slate-700" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{company.teamTitle}</h3>
                    <p className="mt-2 leading-7 text-gray-600">{company.teamBody}</p>
                  </div>
                </div>
              </div>

              <div className="mt-5 rounded-xl bg-white p-5">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-100">
                    <Smartphone className="h-5 w-5 text-teal-600" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{company.productTitle}</h3>
                    <p className="mt-2 leading-7 text-gray-600">{company.productBody}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gray-50 px-4 py-16">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 max-w-3xl">
              <h2 className="text-3xl font-semibold text-slate-900 md:text-4xl">{company.focusTitle}</h2>
              {company.focusIntro && (
                <p className="mt-4 text-lg leading-8 text-gray-600">{company.focusIntro}</p>
              )}
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {capabilities.map((item) => {
                const Icon = item.icon;

                return (
                  <article key={item.title} className="rounded-2xl bg-white p-6 shadow-sm">
                    <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-teal-100">
                      <Icon className="h-6 w-6 text-teal-600" aria-hidden="true" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">{item.title}</h3>
                    <p className="mt-3 leading-7 text-gray-600">{item.desc}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="px-4 py-14">
          <div className="mx-auto max-w-4xl text-center">
            <Link
              to="/"
              className="inline-flex rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-slate-700"
            >
              {company.backToVitalo}
            </Link>
          </div>
        </section>
      </main>

      <footer className="border-t bg-white">
        <div className="mx-auto max-w-6xl px-4 py-8">
          <p className="text-center text-sm text-gray-500">
            © {new Date().getFullYear()} Vitalo. {translations.footer.rights}
          </p>
          <p className="mx-auto mt-3 max-w-2xl text-center text-xs leading-relaxed text-gray-400">
            {translations.footer.operator}
          </p>
        </div>
      </footer>
    </div>
  );
}

function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    if (window.plausible) {
      window.plausible('pageview');
    }
  }, [location.pathname]);

  return null;
}

function App() {
  return (
    <Router>
      <PageViewTracker />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/company" element={<CompanyPage />} />
        <Route path="/ar/company" element={<CompanyPage />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/ar/contact" element={<ContactAr />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/ar/privacy-policy" element={<PrivacyPolicyAr />} />
        <Route path="/ar/terms" element={<TermsAr />} />
      </Routes>
    </Router>
  );
}

export default App;
